import React, { useCallback, useEffect, useState, useRef, memo, Component } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getTeacherStudents } from '../../utils/teacherUtils';
import { addStudentToTeacher, updateStudent, deleteStudentAccount } from '../../services/studentService';
import { HomeIcon, BookOpenIcon, UsersIcon, BarChartIcon, CalendarIcon, SettingsIcon, SearchIcon, UserIcon, MusicIcon, PhoneIcon, MailIcon, ExternalLinkIcon, PlusIcon, MessageSquareIcon, LoaderIcon, ChevronDownIcon, ChevronUpIcon, StickyNoteIcon } from 'lucide-react';
import { InstrumentFilterDropdown } from '../InstrumentFilterDropdown';
import { StatusFilterDropdown } from '../StatusFilterDropdown';
import { AddStudentModal } from '../modals/AddStudentModal';
import { ViewStudentProfilePanel } from '../modals/ViewStudentProfilePanel';
import { useTheme } from '../../components/providers/ThemeProvider';
import toast from 'react-hot-toast';
// Local storage key for students
const STUDENTS_STORAGE_KEY = 'mc_students_temp';
interface StudentType {
  id: string;
  studentId: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  instrument?: string;
  instruments?: string[];
  notes?: string;
  profilePicture?: string;
  avatar?: string;
  status?: string;
  nextLesson?: string;
  grade?: number;
}
export const MyStudents = () => {
  const {
    currentUser,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [students, setStudents] = useState<StudentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  // Track whether the initial data fetch has completed to avoid showing
  // a duplicate spinner right after AuthGuard's spinner disappears
  const initialFetchDone = useRef(false);
  // State to track expanded notes for each student
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});
  // State for add student modal
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  // State for view profile panel
  const [isViewProfilePanelOpen, setIsViewProfilePanelOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentType | null>(null);
  // Toggle notes expansion
  const toggleNotesExpansion = (studentId: string) => {
    setExpandedNotes(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };
  // Function to save students to localStorage
  const saveStudentsToLocalStorage = (studentsList: StudentType[]) => {
    try {
      localStorage.setItem(STUDENTS_STORAGE_KEY, JSON.stringify(studentsList));
    } catch (error) {
      console.error('Error saving students to localStorage:', error);
    }
  };
  // Function to load students from localStorage
  const loadStudentsFromLocalStorage = (): StudentType[] => {
    try {
      const storedStudents = localStorage.getItem(STUDENTS_STORAGE_KEY);
      if (storedStudents) {
        return JSON.parse(storedStudents);
      }
    } catch (error) {
      console.error('Error loading students from localStorage:', error);
    }
    return [];
  };
  // Fetch students from Firestore with automatic data transformation
  // Wrapped in useCallback to prevent infinite loops
  const fetchStudents = useCallback(async () => {
    // CRITICAL FIX: Don't fetch if auth is still loading
    if (authLoading) {
      console.log('fetchStudents: Auth still loading, skipping fetch');
      return;
    }
    if (!currentUser) {
      console.log('fetchStudents: No current user');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('fetchStudents: Fetching students for teacher:', currentUser.uid);
      // getTeacherStudents now automatically transforms flat Firestore data
      // into nested objects (address, guardian) that the UI expects
      const fetchedStudents = await getTeacherStudents(currentUser.uid);
      console.log('fetchStudents: Successfully fetched and transformed students:', {
        count: fetchedStudents.length,
        sampleStudent: fetchedStudents[0] ? {
          id: fetchedStudents[0].id,
          name: fetchedStudents[0].name,
          hasAddress: !!fetchedStudents[0].address,
          hasGuardian: !!fetchedStudents[0].guardian,
          addressPreview: fetchedStudents[0].address?.street,
          guardianPreview: fetchedStudents[0].guardian?.name
        } : null
      });
      setStudents(fetchedStudents);
    } catch (err) {
      console.error('fetchStudents: Error fetching students:', err);
      setError('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
      initialFetchDone.current = true;
    }
  }, [currentUser, authLoading]);
  // Fetch students on component mount - ONLY when auth is ready
  useEffect(() => {
    if (!authLoading) {
      fetchStudents();
    }
  }, [fetchStudents, authLoading]);
  // Listen for the custom studentDeleted event
  useEffect(() => {
    const handleStudentDeleted = event => {
      console.log('Student deleted event received, refreshing student list');
      // Get the deleted student ID from the event if available
      const deletedStudentId = event.detail?.studentId;
      if (deletedStudentId) {
        // Update both state and localStorage by removing the deleted student
        setStudents(prevStudents => {
          const updatedStudents = prevStudents.filter(s => s.id !== deletedStudentId && s.studentId !== deletedStudentId);
          saveStudentsToLocalStorage(updatedStudents);
          return updatedStudents;
        });
      } else {
        // If no specific ID, refresh all students
        try {
          fetchStudents();
        } catch (error) {
          console.error('Error refreshing after student deletion:', error);
        }
      }
    };
    window.addEventListener('studentDeleted', handleStudentDeleted);
    return () => {
      window.removeEventListener('studentDeleted', handleStudentDeleted);
    };
  }, [fetchStudents]);
  // Check if we need to refresh students from navigation state
  useEffect(() => {
    if (location?.state?.refreshStudents) {
      console.log('Refreshing students list from navigation state');
      try {
        fetchStudents();
        // Clear the state to prevent unnecessary refreshes
        navigate(location.pathname, {
          replace: true,
          state: {}
        });
      } catch (error) {
        console.error('Error refreshing students from navigation:', error);
      }
    }
  }, [location, fetchStudents, navigate]);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Add student using Firebase
  const handleAddStudent = async newStudent => {
    if (!currentUser) {
      console.error('No current user found');
      setError('You must be logged in to add a student.');
      return;
    }
    console.log('Adding student with data:', newStudent);
    try {
      setLoading(true);
      const studentData = {
        studentId: newStudent.studentId || `manual-${Date.now()}`,
        name: newStudent.name,
        firstName: newStudent.firstName || '',
        lastName: newStudent.lastName || '',
        email: newStudent.email || '',
        phone: newStudent.phone || '',
        instrument: newStudent.instrument || '',
        grade: newStudent.grade,
        lessonType: newStudent.lessonType || '',
        timeZone: newStudent.timeZone || '',
        addressStreet: newStudent.addressStreet || '',
        addressApt: newStudent.addressApt || '',
        addressCity: newStudent.addressCity || '',
        addressState: newStudent.addressState || '',
        addressZip: newStudent.addressZip || '',
        guardianName: newStudent.guardianName || '',
        guardianEmail: newStudent.guardianEmail || '',
        guardianPhone: newStudent.guardianPhone || '',
        guardianPreferredContact: newStudent.guardianPreferredContact || '',
        status: newStudent.status || 'active',
        notes: newStudent.notes || '',
        profilePicture: newStudent.profilePicture || ''
      };
      console.log('Calling addStudentToTeacher with teacherId:', currentUser.uid);
      await addStudentToTeacher(currentUser.uid, studentData);
      console.log('Student added successfully to Firebase');
      // Refresh the students list from Firebase
      await fetchStudents();
      console.log('Students list refreshed');
    } catch (err) {
      console.error('Error adding new student:', err);
      setError(`Failed to add student: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  // Navigate to messaging with student
  const handleMessageStudent = studentId => {
    if (!studentId) {
      console.error('Cannot message student: studentId is undefined');
      return;
    }
    navigate(`/teacher-messages?studentId=${studentId}`);
  };
  // Filter students based on search query, selected instruments, and status
  const filteredStudents = students.filter(student => {
    if (!student) return false;
    // Filter by search query
    const matchesSearch = searchQuery === '' || student.name && student.name.toLowerCase().includes(searchQuery.toLowerCase()) || student.email && student.email.toLowerCase().includes(searchQuery.toLowerCase());
    // Filter by instruments
    const matchesInstruments = selectedInstruments.length === 0 || student.instruments && Array.isArray(student.instruments) && student.instruments.some(instrument => selectedInstruments.includes(instrument));
    // Filter by status
    const matchesStatus = selectedStatus === 'all' || student.status === selectedStatus;
    return matchesSearch && matchesInstruments && matchesStatus;
  });
  // Get status badge class - updated to use isDark
  const getStatusBadgeClass = status => {
    if (!status) return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    switch (status) {
      case 'active':
        return isDark ? 'bg-green-500/20 text-green-500 border border-green-500/30' : 'bg-green-500/10 text-green-500 border border-green-500/30';
      case 'pending':
        return isDark ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
      case 'trial':
        return isDark ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      default:
        return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  // Handle opening the view profile panel
  const handleViewProfile = (student: StudentType) => {
    setSelectedStudent(student);
    setIsViewProfilePanelOpen(true);
  };
  // Handle closing the view profile panel
  const handleCloseViewProfile = () => {
    setIsViewProfilePanelOpen(false);
    setSelectedStudent(null);
  };
  // Update student using Firebase with improved error handling
  const handleEditStudent = async (studentId: string, updatedData: any) => {
    if (!currentUser) {
      console.error('handleEditStudent: No current user found');
      toast.error('You must be logged in to update student information');
      return;
    }
    console.log('handleEditStudent called with:', {
      studentId,
      teacherId: currentUser.uid,
      updatedData
    });
    try {
      // Prepare the update payload - ALWAYS flatten nested objects
      const updatePayload: any = {};
      // Basic fields - copy directly
      if (updatedData.firstName !== undefined) updatePayload.firstName = updatedData.firstName;
      if (updatedData.lastName !== undefined) updatePayload.lastName = updatedData.lastName;
      if (updatedData.name !== undefined) updatePayload.name = updatedData.name;
      if (updatedData.email !== undefined) updatePayload.email = updatedData.email;
      if (updatedData.phone !== undefined) updatePayload.phone = updatedData.phone;
      if (updatedData.instrument !== undefined) updatePayload.instrument = updatedData.instrument;
      if (updatedData.grade !== undefined) updatePayload.grade = updatedData.grade;
      if (updatedData.notes !== undefined) updatePayload.notes = updatedData.notes;
      if (updatedData.lessonType !== undefined) updatePayload.lessonType = updatedData.lessonType;
      if (updatedData.timeZone !== undefined) updatePayload.timeZone = updatedData.timeZone;
      // Handle address - CRITICAL: flatten nested object to match Firestore schema
      if (updatedData.address && typeof updatedData.address === 'object') {
        updatePayload.addressStreet = updatedData.address.street || '';
        updatePayload.addressApt = updatedData.address.apt || '';
        updatePayload.addressCity = updatedData.address.city || '';
        updatePayload.addressState = updatedData.address.state || '';
        updatePayload.addressZip = updatedData.address.zip || '';
      } // Handle guardian - CRITICAL: flatten nested object to match Firestore schema
      if (updatedData.guardian && typeof updatedData.guardian === 'object') {
        updatePayload.guardianName = updatedData.guardian.name || '';
        updatePayload.guardianEmail = updatedData.guardian.email || '';
        updatePayload.guardianPhone = updatedData.guardian.phone || '';
        updatePayload.guardianPreferredContact = updatedData.guardian.preferredContact || '';
        updatePayload.guardianIsEmergencyContact = updatedData.guardian.isEmergencyContact || false;
      }
      console.log('Flattened update payload for Firestore:', updatePayload);
      // Call the service function with flattened data
      await updateStudent(currentUser.uid, studentId, updatePayload);
      console.log('Student updated successfully in Firestore');
      // Show success toast
      toast.success('Student profile updated successfully!');
      // Refresh the students list from Firebase
      // This will automatically transform the flat Firestore data back to nested objects
      await fetchStudents();
      console.log('Students list refreshed with transformed data');
    } catch (error) {
      console.error('handleEditStudent: Error updating student:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      // Show error toast with specific message
      const errorMessage = error.message || 'Failed to update student. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  };
  // Delete student using Firebase
  const handleDeleteStudent = async (studentId: string) => {
    if (!currentUser) return;
    try {
      await deleteStudentAccount(studentId, currentUser.uid);
      setStudents(prevStudents => prevStudents.filter(s => s.id !== studentId && s.studentId !== studentId));
      handleCloseViewProfile();
    } catch (error) {
      console.error('Error deleting student:', error);
      setError('Failed to delete student. Please try again.');
    }
  };
  // Render the component with error handling
  try {
    return <>
        <div className="mx-auto" data-id="element-5515">
          {/* Header */}
          <div className="mb-6 flex justify-between items-center" data-id="element-5516">
            <div data-id="element-5517">
              <h1 className={`text-3xl font-serif font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5518">
                My Students
              </h1>
              <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5519">
                Manage and view all your student profiles
              </p>
              <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5520">
                Total Students: {students.length}
              </p>
            </div>
            <button onClick={() => setIsAddStudentModalOpen(true)} className={`px-4 py-2 rounded-md border-2 transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-5521">
              Add Student
            </button>
          </div>
          {/* Filters */}
          <div className={`${isDark ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-4 mb-6 transition-colors duration-300`} data-id="element-5522">
            <div className="flex flex-wrap gap-4" data-id="element-5523">
              {/* Search */}
              <div className="flex-1 min-w-[200px]" data-id="element-5524">
                <div className={`flex items-center px-3 py-2 rounded-lg ${isDark ? 'bg-navy-900/50' : 'bg-gray-100'}`} data-id="element-5525">
                  <SearchIcon className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'} mr-2`} data-id="element-5526" />
                  <input type="text" placeholder="Search students..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`bg-transparent border-none focus:ring-0 focus:outline-none w-full ${isDark ? 'text-white placeholder-gray-400' : 'text-gray-800 placeholder-gray-500'}`} data-id="element-5527" />
                </div>
              </div>
              {/* Instrument Filter */}
              <InstrumentFilterDropdown selectedInstruments={selectedInstruments} onChange={setSelectedInstruments} darkMode={isDark} data-id="element-5528" />
              {/* Status Filter */}
              <StatusFilterDropdown selectedStatus={selectedStatus} onChange={setSelectedStatus} darkMode={isDark} data-id="element-5529" />
            </div>
          </div>
          {/* Loading State - only show for subsequent loads, not the initial fetch
               (AuthGuard already shows a spinner during auth loading, so showing
               another spinner immediately on mount creates a "dual spinner" effect) */}
          {loading && !authLoading && initialFetchDone.current && <div className="flex justify-center items-center py-12" data-id="element-5530">
              <LoaderIcon className="h-8 w-8 text-green-500 animate-spin" data-id="element-5531" />
              <span className={`ml-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5532">
                Loading students...
              </span>
            </div>}
          {/* Error State */}
          {error && !loading && <div className={`${isDark ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-600'} p-4 rounded-xl mb-6`} data-id="element-5533">
              <p data-id="element-5534">{error}</p>
              <button onClick={() => {
            setError(null);
            fetchStudents();
          }} className="mt-2 underline hover:text-red-400" data-id="element-5535">
                Retry
              </button>
            </div>}
          {/* Students List */}
          {!loading && !error && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5536">
              {filteredStudents && filteredStudents.length > 0 ? filteredStudents.map(student => student && <div key={student.id || Math.random().toString()} className={`${isDark ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm overflow-hidden transition-transform duration-300 hover:shadow-md transform hover:-translate-y-1 flex flex-col`} style={{
            willChange: 'transform'
          }} data-id="element-5537">
                        <div className="p-6 flex flex-col flex-grow" data-id="element-5538">
                          {/* Top content section - will grow to fill space */}
                          <div className="flex-grow" data-id="element-5539">
                            <div className="flex items-start justify-between mb-4" data-id="element-5540">
                              <div className="flex items-center" data-id="element-5541">
                                <div className="h-12 w-12 rounded-full overflow-hidden bg-gradient-to-br from-deep-pink to-burgundy mr-4" data-id="element-5542">
                                  {student.avatar ? <img src={student.avatar} alt={student.name || 'Student'} className="h-full w-full object-cover" onError={e => {
                        e.currentTarget.parentElement.innerHTML = `
                                            <div class="h-full w-full flex items-center justify-center text-white text-xl font-bold">
                                              ${student.name ? student.name.charAt(0) : 'S'}
                                            </div>
                                          `;
                      }} data-id="element-5543" /> : <div className="h-full w-full flex items-center justify-center text-white text-xl font-bold" data-id="element-5544">
                                      {student.name ? student.name.charAt(0) : 'S'}
                                    </div>}
                                </div>
                                <div data-id="element-5545">
                                  <div className="flex items-center gap-2" data-id="element-5546">
                                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5547">
                                      {student.name || 'Unnamed Student'}
                                    </h3>
                                  </div>
                                  <div className="flex flex-wrap gap-1 mt-1" data-id="element-5548">
                                    {student.instruments && Array.isArray(student.instruments) ? student.instruments.map((instrument, idx) => <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-navy-900/70 text-gray-300' : 'bg-gray-100 text-gray-800'}`} data-id="element-5549">
                                            {instrument}
                                            {student.grade && ` · Grade ${student.grade}`}
                                          </span>) : <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-navy-900/70 text-gray-300' : 'bg-gray-100 text-gray-800'}`} data-id="element-5550">
                                        {student.instrument || 'Music'}
                                        {student.grade && ` · Grade ${student.grade}`}
                                      </span>}
                                  </div>
                                </div>
                              </div>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(student.status)}`} data-id="element-5551">
                                {student.status ? student.status.charAt(0).toUpperCase() + student.status.slice(1) : 'Unknown'}
                              </span>
                            </div>
                            <div className="space-y-2" data-id="element-5552">
                              {student.email && <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5553">
                                  <MailIcon className="h-4 w-4 mr-2" data-id="element-5554" />
                                  <button onClick={() => {
                      const subject = encodeURIComponent('Student Information');
                      const body = encodeURIComponent(`Regarding student: ${student.name || 'Student'}`);
                      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(student.email)}&su=${subject}&body=${body}`;
                      window.open(gmailUrl, '_blank');
                    }} className={`text-sm truncate transition-all text-left ${isDark ? 'hover:text-white hover:brightness-110' : 'hover:text-gray-900 hover:underline'}`} data-id="element-5555">
                                    {student.email}
                                  </button>
                                </div>}
                              {student.phone && <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5556">
                                  <PhoneIcon className="h-4 w-4 mr-2" data-id="element-5557" />
                                  <span className="text-sm" data-id="element-5558">
                                    {student.phone}
                                  </span>
                                </div>}
                              {student.nextLesson && <div className={`flex items-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5559">
                                  <CalendarIcon className="h-4 w-4 mr-2" data-id="element-5560" />
                                  <span className="text-sm" data-id="element-5561">
                                    Next: {student.nextLesson}
                                  </span>
                                </div>}
                              {/* Notes section with expand/collapse */}
                              {student.notes && <div className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5562">
                                  <div className="flex items-start" data-id="element-5563">
                                    <StickyNoteIcon className={`h-4 w-4 mr-2 mt-0.5`} style={{
                        color: isDark ? '#B0B3C1' : '#4A4A4A'
                      }} data-id="element-5564" />
                                    <div className="flex-1" data-id="element-5565">
                                      <p className="text-sm font-medium mb-1" style={{
                          color: isDark ? '#B0B3C1' : '#4A4A4A'
                        }} data-id="element-5566">
                                        Notes:
                                      </p>
                                      <div className="text-sm" data-id="element-5567">
                                        {expandedNotes[student.id] ? student.notes : student.notes.split(' ').slice(0, 4).join(' ') + (student.notes.split(' ').length > 4 ? '...' : '')}
                                        {student.notes.split(' ').length > 4 && <button onClick={() => toggleNotesExpansion(student.id)} className="text-xs ml-1 inline-flex items-center" style={{
                            color: isDark ? '#4DA6FF' : '#007BFF'
                          }} data-id="element-5568">
                                            {expandedNotes[student.id] ? <>
                                                View less
                                                <ChevronUpIcon className="h-3 w-3 ml-0.5" data-id="element-5569" />
                                              </> : <>
                                                View more
                                                <ChevronDownIcon className="h-3 w-3 ml-0.5" data-id="element-5570" />
                                              </>}
                                          </button>}
                                      </div>
                                    </div>
                                  </div>
                                </div>}
                            </div>
                          </div>
                          {/* Bottom buttons section - always at bottom */}
                          <div className="mt-4 flex justify-between" data-id="element-5571">
                            {/* HIDDEN: Message button - messaging feature not ready
                                 <button
                                 onClick={() =>
                                 handleMessageStudent(
                                 student.studentId || student.id,
                                 )
                                 }
                                 className="inline-flex items-center text-deep-pink hover:text-burgundy text-sm font-medium transition-colors"
                                 >
                                 <MessageSquareIcon className="h-4 w-4 mr-1" />
                                 Message
                                 </button>
                                 */}
                            <div data-id="element-5572"></div> {/* Spacer to maintain layout */}
                            <button onClick={() => handleViewProfile(student)} className={`inline-flex items-center text-sm font-medium transition-colors ${isDark ? 'text-[#3B82F6] hover:text-[#60A5FA]' : 'text-[#3B82F6] hover:text-[#2563EB]'}`} data-id="element-5573">
                              View Profile
                              <ExternalLinkIcon className="h-4 w-4 ml-1" data-id="element-5574" />
                            </button>
                          </div>
                        </div>
                      </div>) : <div className={`col-span-1 md:col-span-2 lg:col-span-3 ${isDark ? 'bg-navy-800/80' : 'bg-white'} rounded-xl p-8 text-center`} data-id="element-5575">
                  <UserIcon className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-5576" />
                  <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5577">
                    No students found
                  </h3>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-4`} data-id="element-5578">
                    {students.length === 0 ? "You haven't added any students yet" : 'Try adjusting your search or filter criteria'}
                  </p>
                  {students.length > 0 && <button onClick={() => {
              setSearchQuery('');
              setSelectedInstruments([]);
              setSelectedStatus('all');
            }} className="text-deep-pink hover:text-burgundy font-medium transition-colors" data-id="element-5579">
                      Reset Filters
                    </button>}
                </div>}
            </div>}
        </div>
        {/* Add Student Modal */}
        <AddStudentModal isOpen={isAddStudentModalOpen} onClose={() => setIsAddStudentModalOpen(false)} onSuccess={handleAddStudent} student={null} darkMode={isDark} data-id="element-5580" />
        {/* View Student Profile Panel */}
        <ViewStudentProfilePanel isOpen={isViewProfilePanelOpen} onClose={handleCloseViewProfile} student={selectedStudent} darkMode={isDark} onEdit={handleEditStudent} onDelete={handleDeleteStudent} onMessage={handleMessageStudent} data-id="element-5581" />
      </>;
  } catch (renderError) {
    console.error('Error rendering MyStudents component:', renderError);
    // Fallback minimal UI in case of render errors
    return <div className="px-4 py-6" data-id="element-5582">
        <div className="max-w-7xl mx-auto" data-id="element-5583">
          <h1 className={`text-3xl font-serif font-bold ${isDark ? 'text-white' : 'text-gray-800'} mb-6`} data-id="element-5584">
            My Students
          </h1>
          <div className="bg-red-900/30 text-red-200 p-6 rounded-xl" data-id="element-5585">
            <h2 className="text-xl font-medium mb-2" data-id="element-5586">Something went wrong</h2>
            <p className="mb-4" data-id="element-5587">
              We encountered an error while loading your students. Please try
              refreshing the page.
            </p>
            <button onClick={() => window.location.reload()} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-4 rounded-lg transition-colors" data-id="element-5588">
              Refresh Page
            </button>
          </div>
        </div>
      </div>;
  }
};